import { GUARDIAN_COLORS, POI_LAYOUTS, POI_RULE, SIMPATIA_CHESTS, type NaturalChestKind, type SimpatiaChestKind } from "@eldrun/content";
import { dcos, dsin, hash32 } from "@eldrun/shared";
import { poiPoint, type Poi } from "./pois.ts";

/** Colisores dos adereços dos POIs: os mesmos no servidor e na predição do cliente. */
export interface PoiColliders {
  cylinders: { id: number; x: number; z: number; radius: number; bottom: number; top: number }[];
  /** `ceiling`: caixa suspensa (a laje da Caverna), fora do piso onde as coisas caem (`floorHeight`). */
  boxes: { id: number; minX: number; maxX: number; minY: number; maxY: number; minZ: number; maxZ: number; ceiling?: boolean }[];
}

/** Ids dos colisores dos POIs: acima dos recursos (até 2²⁸) e abaixo das estruturas (2³⁰). */
export const POI_COLLIDER_BASE = 536870912;

const QUARTER = 1.5707963267948966;

/**
 * Dente máximo da escada (m): as caixas de um POI que gira livre viram faixas alinhadas aos eixos do
 * mundo, e nos lados nenhum ponto das caixas fica mais longe que isto de uma faixa (nos cantos das
 * pontas, até isto·√2, 0,21 m; o raio do jogador é 0,35 m).
 */
const STRIP_GAP = 0.15;
/** Faixa mais estreita que isto (m) através do corte não entra. */
const MIN_STRIP = 0.05;
/** Cantos mais perto que isto (m) ao longo do corte contam como um só (sem lasca de faixa entre eles). */
const SAME_CUT = 0.01;

type Rect = { minX: number; maxX: number; minZ: number; maxZ: number };
/** Caixa nos eixos locais do POI: centro no mundo e meias medidas (x e z locais). */
export type TurnedBox = { cx: number; cz: number; hw: number; hd: number };

/**
 * Faixas das caixas num corte ao longo de u (x ou z do mundo), com v o outro eixo; (a1, b1) e (a2, b2)
 * dão o x e o z locais em função de (u − cu, v − cv), uma rotação. Os cantos de todas as caixas dividem
 * o corte em trechos; em cada um, a largura das faixas vem do lado mais inclinado que o cruza (um lado de
 * inclinação θ deixa fora de uma faixa de largura W um dente de no máximo W·sen θ). Em cada faixa, cada
 * caixa dá o intervalo de v em que está nas duas pontas (a caixa é convexa: está na faixa toda), e os
 * intervalos que se tocam viram uma caixa só.
 */
function stripsAlong(boxes: readonly { cu: number; cv: number; hw: number; hd: number }[], a1: number, b1: number, a2: number, b2: number): { u0: number; u1: number; lo: number; hi: number }[] {
  // Os lados x = ±hw correm na direção (a2, b2) e os z = ±hd na (a1, b1), vetores unitários: |b| é o
  // seno da inclinação. O trecho do meio de cada caixa fica entre os lados de maior projeção em u.
  const end = Math.max(Math.abs(b1), Math.abs(b2));
  const views = boxes.map(({ cu, cv, hw, hd }) => ({
    corners: [hw * a1 + hd * a2, hw * a1 - hd * a2, -hw * a1 + hd * a2, -hw * a1 - hd * a2].map((d) => cu + d).sort((p, q) => p - q),
    middle: hd * Math.abs(a2) >= hw * Math.abs(a1) ? Math.abs(b2) : Math.abs(b1),
    /** Intervalo de v em que (u, v) fica na caixa (vazio se lo > hi). */
    span(u: number): [number, number] {
      let lo = -Infinity;
      let hi = Infinity;
      for (const [a, b, h] of [[a1, b1, hw], [a2, b2, hd]] as const) {
        const base = (u - cu) * a;
        // Lado paralelo ao corte: as pontas das faixas caem nele (com o arredondamento de u − cu).
        if (Math.abs(b) < 1e-9) {
          if (base < -h - 1e-9 || base > h + 1e-9) return [Infinity, -Infinity];
          continue;
        }
        const p = (-h - base) / b;
        const q = (h - base) / b;
        lo = Math.max(lo, Math.min(p, q));
        hi = Math.min(hi, Math.max(p, q));
      }
      return [cv + lo, cv + hi];
    },
  }));
  // Trechos entre os cantos. O estreito (menos de `MIN_STRIP`) entre cantos do meio das caixas se junta ao
  // anterior (o intervalo de cada caixa vale nas pontas de qualquer faixa); o que encosta na ponta de uma
  // caixa fica sozinho, para a faixa da ponta, que não dá caixa, ser estreita.
  // Cantos a menos de `SAME_CUT` viram o primeiro deles (o trecho entre eles some sem abrir vão: os
  // vizinhos se encontram ali); a ponta de qualquer um faz do canto uma ponta.
  const cuts: number[] = [];
  const tips = new Set<number>();
  const marked = views.flatMap((v) => v.corners.map((u, k) => ({ u, tip: k === 0 || k === 3 }))).sort((p, q) => p.u - q.u || Number(q.tip) - Number(p.tip));
  for (const { u, tip } of marked) {
    if (cuts.length === 0 || u - cuts[cuts.length - 1]! >= SAME_CUT) cuts.push(u);
    if (tip) tips.add(cuts[cuts.length - 1]!);
  }
  const segments: [number, number][] = [];
  for (let k = 0; k + 1 < cuts.length; k++) {
    const [from, to] = [cuts[k]!, cuts[k + 1]!];
    const last = segments[segments.length - 1];
    if (to - from < MIN_STRIP && !tips.has(from) && !tips.has(to) && last && last[1] === from) last[1] = to;
    else segments.push([from, to]);
  }
  const out: { u0: number; u1: number; lo: number; hi: number }[] = [];
  for (const [from, to] of segments) {
    const len = to - from;
    // A caixa que entra no trecho e tem nele um pedaço de ponta pede a largura da ponta (lado íngreme).
    const active = views.filter((v) => v.corners[0]! < to && v.corners[3]! > from);
    if (active.length === 0) continue;
    const sine = Math.max(...active.map((v) => (from < v.corners[1]! || to > v.corners[2]! ? end : v.middle)));
    const n = sine > 0 ? Math.max(1, Math.ceil((len * sine) / STRIP_GAP)) : 1;
    for (let j = 0; j < n; j++) {
      const u0 = from + (len * j) / n;
      const u1 = from + (len * (j + 1)) / n;
      const spans: [number, number][] = [];
      for (const v of active) {
        const [lo0, hi0] = v.span(u0);
        const [lo1, hi1] = v.span(u1);
        const lo = Math.max(lo0, lo1);
        const hi = Math.min(hi0, hi1);
        if (hi - lo >= MIN_STRIP) spans.push([lo, hi]);
      }
      spans.sort((p, q) => p[0] - q[0] || p[1] - q[1]);
      let open: [number, number] | undefined;
      for (const [lo, hi] of spans) {
        if (open && lo <= open[1]) open[1] = Math.max(open[1], hi);
        else {
          if (open) out.push({ u0, u1, lo: open[0], hi: open[1] });
          open = [lo, hi];
        }
      }
      if (open) out.push({ u0, u1, lo: open[0], hi: open[1] });
    }
  }
  return out;
}

/**
 * Caixas giradas por (c, s) como em `poiPoint` (o x local aponta para (c, −s), o z local para (s, c)) em
 * faixas alinhadas aos eixos do mundo, todas dentro delas (escada inscrita: nenhum ponto de colisor fora
 * das caixas), cortando o eixo que dá menos faixas; as sobrepostas não repetem faixa. Sem giro (ou de 90°
 * em 90°), cada caixa é ela mesma. Só soma, produto, divisão, `ceil`, máximo e ordenação: igual no
 * servidor e no cliente.
 */
export function inscribedStrips(c: number, s: number, boxes: readonly TurnedBox[]): Rect[] {
  // Alinhada aos eixos: exata, sem escada (as escadas de cada caixa sozinha, sobrepostas, deixariam
  // buracos onde elas mal se cruzam; por isso, girada, a escada é sempre a das caixas juntas).
  if (Math.abs(s) < 1e-12 || Math.abs(c) < 1e-12) {
    return boxes.map(({ cx, cz, hw, hd }) => {
      const [w, d] = Math.abs(s) < 1e-12 ? [hw, hd] : [hd, hw];
      return { minX: cx - w, maxX: cx + w, minZ: cz - d, maxZ: cz + d };
    });
  }
  // Local x = c·dx − s·dz e local z = s·dx + c·dz (dx, dz: do centro da caixa ao ponto).
  const inX = stripsAlong(boxes.map((b) => ({ cu: b.cx, cv: b.cz, hw: b.hw, hd: b.hd })), c, -s, s, c);
  const inZ = stripsAlong(boxes.map((b) => ({ cu: b.cz, cv: b.cx, hw: b.hw, hd: b.hd })), -s, c, c, s);
  if (inX.length <= inZ.length) return inX.map(({ u0, u1, lo, hi }) => ({ minX: u0, maxX: u1, minZ: lo, maxZ: hi }));
  return inZ.map(({ u0, u1, lo, hi }) => ({ minX: lo, maxX: hi, minZ: u0, maxZ: u1 }));
}

/**
 * Colisores dos adereços (barracas, paredes da ruína, pilares e teto da gruta, pedras do santuário),
 * na altura do terreno já carimbado. Caixas nos POIs girados de 90° em 90° (ruínas): o giro troca
 * largura e profundidade. Nos que giram livre (a laje da Caverna), as caixas da mesma faixa de altura
 * viram juntas a escada de faixas alinhadas dentro delas (`inscribedStrips`), na altura do chão do
 * centro do POI (onde fica a montagem). Caixa com base suspensa (`y`) é teto (`ceiling`).
 */
export function poiColliders(pois: readonly Poi[], height: (x: number, z: number) => number): PoiColliders {
  const out: PoiColliders = { cylinders: [], boxes: [] };
  let id = POI_COLLIDER_BASE;
  for (const poi of pois) {
    const layout = POI_LAYOUTS[poi.type];
    const groups = new Map<string, { minY: number; maxY: number; ceiling: boolean; boxes: TurnedBox[] }>();
    for (const prop of layout.props) {
      const c = prop.collider;
      if (!c) continue;
      const p = poiPoint(poi, prop);
      if (c.kind === "cilindro") {
        const y = height(p.x, p.z);
        out.cylinders.push({ id: id++, x: p.x, z: p.z, radius: c.radius, bottom: y - 0.5, top: y + c.height });
        continue;
      }
      // No chão, a caixa entra 0,5 m na terra; suspensa, vai da base ao topo.
      const y = layout.rightAngles ? height(p.x, p.z) : height(poi.x, poi.z);
      const [minY, maxY] = c.y === undefined ? [y - 0.5, y + c.h] : [y + c.y, y + c.y + c.h];
      if (layout.rightAngles) {
        const odd = Math.round(poi.rot / QUARTER) % 2 !== 0;
        const hw = (odd ? c.d : c.w) / 2;
        const hd = (odd ? c.w : c.d) / 2;
        out.boxes.push({ id: id++, minX: p.x - hw, maxX: p.x + hw, minY, maxY, minZ: p.z - hd, maxZ: p.z + hd, ...(c.y === undefined ? {} : { ceiling: true }) });
        continue;
      }
      const key = `${c.y ?? "chão"}:${c.h}`;
      const group = groups.get(key) ?? { minY, maxY, ceiling: c.y !== undefined, boxes: [] };
      group.boxes.push({ cx: p.x, cz: p.z, hw: c.w / 2, hd: c.d / 2 });
      groups.set(key, group);
    }
    for (const g of groups.values()) {
      for (const r of inscribedStrips(dcos(poi.rot), dsin(poi.rot), g.boxes)) out.boxes.push({ id: id++, ...r, minY: g.minY, maxY: g.maxY, ...(g.ceiling ? { ceiling: true } : {}) });
    }
  }
  return out;
}

const hashF64 = new Float64Array(1);
const hashWords = new Uint32Array(hashF64.buffer);
const bits = (value: number): [number, number] => {
  hashF64[0] = value;
  return [hashWords[0] as number, hashWords[1] as number];
};

/**
 * Soma de verificação dos colisores (ids e cada coordenada, bit a bit): o golden do worldgen e a
 * conferência do navegador (e2e `island`) contra ele.
 */
export function poiCollidersHash(c: PoiColliders): number {
  let h = hash32(c.cylinders.length, c.boxes.length);
  for (const k of c.cylinders) h = hash32(h, k.id, ...bits(k.x), ...bits(k.z), ...bits(k.radius), ...bits(k.bottom), ...bits(k.top));
  for (const b of c.boxes) h = hash32(h, b.id, ...bits(b.minX), ...bits(b.maxX), ...bits(b.minY), ...bits(b.maxY), ...bits(b.minZ), ...bits(b.maxZ));
  return h;
}

/** Baú natural de um POI (o id é estável: ordem dos POIs e das âncoras). */
export interface PoiChest {
  id: number;
  kind: NaturalChestKind;
  x: number;
  z: number;
}

/** Baús naturais nas âncoras dos POIs (acampamentos, ruínas, grutas e destroços; doc 03). */
export function poiChests(pois: readonly Poi[]): PoiChest[] {
  const out: PoiChest[] = [];
  for (const poi of pois) {
    for (const chest of POI_LAYOUTS[poi.type].chests) {
      const p = poiPoint(poi, chest);
      out.push({ id: out.length, kind: chest.kind, x: p.x, z: p.z });
    }
  }
  return out;
}

/** Baús de Simpatia avulsos (o tipo vem no dado do POI). */
export function poiSimpatiaChests(pois: readonly Poi[]): { id: number; kind: SimpatiaChestKind; x: number; z: number }[] {
  const kinds = Object.keys(SIMPATIA_CHESTS) as SimpatiaChestKind[];
  return pois.filter((p) => p.type === "simpatia").map((p, id) => ({ id, kind: kinds[p.data] ?? "simpatia", x: p.x, z: p.z }));
}

/** Um mascate por acampamento, na âncora dele, de frente para a fogueira. */
export function poiMascates(pois: readonly Poi[]): { id: number; x: number; z: number; faceX: number; faceZ: number }[] {
  const out: { id: number; x: number; z: number; faceX: number; faceZ: number }[] = [];
  for (const poi of pois) {
    const anchor = POI_LAYOUTS[poi.type].mascate;
    if (!anchor) continue;
    const p = poiPoint(poi, anchor);
    out.push({ id: out.length, x: p.x, z: p.z, faceX: poi.x, faceZ: poi.z });
  }
  return out;
}

/** Estruturas interativas no centro dos POIs: Carranca, Pedra de Encanto (ou do chefe), altar do Guardião. */
export function poiCenters(pois: readonly Poi[]): { item: string; x: number; z: number; rot: number }[] {
  const out: { item: string; x: number; z: number; rot: number }[] = [];
  for (const poi of pois) {
    const center = POI_LAYOUTS[poi.type].center;
    if (!center) continue;
    const item = center === "pedra-encanto" && poi.data === 1 ? "pedra-chefe" : center === "santuario" ? `santuario-${GUARDIAN_COLORS[poi.data]?.color ?? "verde"}` : center;
    out.push({ item, x: poi.x, z: poi.z, rot: poi.rot });
  }
  return out;
}

/** Minério nas âncoras das grutas (entra no conjunto de recursos, nos dois lados). */
export function poiOres(pois: readonly Poi[]): { poi: number; anchor: number; type: "veio-ferro" | "veio-prata"; x: number; z: number; rot: number }[] {
  const out: { poi: number; anchor: number; type: "veio-ferro" | "veio-prata"; x: number; z: number; rot: number }[] = [];
  for (const poi of pois) {
    POI_LAYOUTS[poi.type].ores.forEach((ore, anchor) => {
      const p = poiPoint(poi, ore);
      out.push({ poi: poi.id, anchor, type: ore.type, x: p.x, z: p.z, rot: poi.rot + anchor });
    });
  }
  return out;
}

/**
 * Pegadas dos POIs num índice de 128 m: recursos (e a grama) ficam de fora delas (doc 04,
 * Carimbos de terreno: "as pegadas também excluem recursos e grama").
 */
export class PoiFootprints {
  private static readonly CELL = 128;
  private static readonly SIDE = 16;
  private readonly cells: { x: number; z: number; r2: number }[][];

  constructor(pois: readonly Poi[]) {
    const { SIDE } = PoiFootprints;
    this.cells = Array.from({ length: SIDE * SIDE }, () => []);
    for (const p of pois) {
      const r = POI_RULE.get(p.type)?.footprint ?? 0;
      if (r <= 0) continue;
      for (let j = this.cell(p.z - r); j <= this.cell(p.z + r); j++) {
        for (let i = this.cell(p.x - r); i <= this.cell(p.x + r); i++) (this.cells[j * SIDE + i] as { x: number; z: number; r2: number }[]).push({ x: p.x, z: p.z, r2: r * r });
      }
    }
  }

  private cell(v: number): number {
    const { CELL, SIDE } = PoiFootprints;
    return Math.min(SIDE - 1, Math.max(0, Math.floor((v + CELL * SIDE * 0.5) / CELL)));
  }

  inside(x: number, z: number): boolean {
    for (const f of this.cells[this.cell(z) * PoiFootprints.SIDE + this.cell(x)] as { x: number; z: number; r2: number }[]) {
      const dx = x - f.x;
      const dz = z - f.z;
      if (dx * dx + dz * dz < f.r2) return true;
    }
    return false;
  }
}
